import React, { useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  BackHandler,
  Image,
  PanResponder,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { captureRef } from 'react-native-view-shot';

import { DeviceScreenCapture } from './deviceScreenCapture';
import {
  CaptureHandle,
  CaptureRect,
  beginFeedbackCapture,
  clampCaptureRect,
  clipCaptureFrame,
  defaultCaptureRect,
  dropQueuedFeedbackNavigation,
  endFeedbackCapture,
  feedbackCropRef,
  feedbackPageRef,
  moveCaptureRect,
  resizeCaptureRect,
  useFeedbackCaptureRequest,
} from './feedbackCapture';
import {
  decodedBase64Bytes,
  FeedbackFileError,
  FeedbackFilePayload,
  feedbackFilePayload,
} from './feedbackFile';
import { colors, fontSize, fontWeight, lineHeight, spacing } from '../../theme';
import { showToast } from '../../utils/toast';

const HANDLE = 18;
const HANDLE_HIT = 44;
const EDGE = 28;
const CAPTURE_FAILED = 'Could not capture the screen.';
const CAPTURE_TIMEOUT_MS = 8000;

interface ScreenCaptureProps {
  onDone: (file: FeedbackFilePayload) => void;
  onDismiss: () => void;
}

function useRectPan(
  apply: (
    origin: CaptureRect,
    dx: number,
    dy: number,
    frame: CaptureRect,
  ) => CaptureRect,
  rectRef: React.MutableRefObject<CaptureRect>,
  frameRef: React.MutableRefObject<CaptureRect>,
  setRect: (rect: CaptureRect) => void,
  onAdjust: () => void,
) {
  const origin = useRef<CaptureRect | null>(null);
  const grant = useRef({ x: 0, y: 0 });
  return useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: () => true,
      onPanResponderTerminationRequest: () => false,
      onShouldBlockNativeResponder: () => true,
      onPanResponderGrant: event => {
        onAdjust();
        origin.current = rectRef.current;
        grant.current = {
          x: event.nativeEvent.pageX,
          y: event.nativeEvent.pageY,
        };
        event.preventDefault?.();
      },
      onPanResponderMove: (event, gesture) => {
        const start = origin.current;
        if (!start) {
          return;
        }
        const pageX = event.nativeEvent.pageX;
        const pageY = event.nativeEvent.pageY;
        const dx = Number.isFinite(pageX)
          ? pageX - grant.current.x
          : gesture.dx;
        const dy = Number.isFinite(pageY)
          ? pageY - grant.current.y
          : gesture.dy;
        setRect(apply(start, dx, dy, frameRef.current));
      },
    }),
  ).current;
}

function pngText(shot: string): string {
  const marker = 'base64,';
  const markerAt = shot.indexOf(marker);
  return (
    markerAt === -1 ? shot : shot.slice(markerAt + marker.length)
  ).replace(/\s/g, '');
}

/**
 * Dotted rectangle over the screen that was open. The crop controls render
 * outside `feedbackPageRef`, so the snapshot does not include them. The
 * selection stops above the Done and Cancel bar so those buttons stay usable.
 */
export const ScreenCapture: React.FC<ScreenCaptureProps> = ({
  onDone,
  onDismiss,
}) => {
  const insets = useSafeAreaInsets();
  const viewport = useWindowDimensions();
  const openingFrame: CaptureRect = {
    x: 0,
    y: 0,
    width: Math.max(viewport.width, 1),
    height: Math.max(viewport.height, 1),
  };
  const initialBarHeight = spacing.formControlHeight + spacing.md;
  const [frame, setFrame] = useState(openingFrame);
  const [hostHeight, setHostHeight] = useState(openingFrame.height);
  const [barHeight, setBarHeight] = useState(initialBarHeight);
  const reservedBottom =
    Math.max(insets.bottom, spacing.md) + barHeight + HANDLE_HIT / 2;
  const bounds = clipCaptureFrame(frame, hostHeight, reservedBottom);
  const [rect, setRect] = useState(() =>
    defaultCaptureRect(
      clipCaptureFrame(
        openingFrame,
        openingFrame.height,
        Math.max(insets.bottom, spacing.md) + initialBarHeight + HANDLE_HIT / 2,
      ),
    ),
  );
  const [shot, setShot] = useState<string | null>(null);
  const [snapping, setSnapping] = useState(false);
  const hostRef = useRef<View>(null);
  const boundsRef = useRef(bounds);
  const reservedRef = useRef(reservedBottom);
  const rectRef = useRef(rect);
  const adjusted = useRef(false);
  const settled = useRef(false);
  const busy = useRef(false);
  const cropStarted = useRef(false);
  const finishCropRef = useRef<() => Promise<void>>(async () => undefined);
  boundsRef.current = bounds;
  reservedRef.current = reservedBottom;
  rectRef.current = rect;

  useEffect(() => {
    if (shot) {
      return;
    }
    let cancelled = false;
    const measure = () => {
      const page = feedbackPageRef.current;
      if (!page || typeof page.measureInWindow !== 'function') {
        return;
      }
      page.measureInWindow((pageX, pageY, pageWidth, pageHeight) => {
        if (cancelled || pageWidth <= 0 || pageHeight <= 0) {
          return;
        }
        const apply = (
          originX: number,
          originY: number,
          measuredHostHeight: number,
        ) => {
          if (cancelled) {
            return;
          }
          // The box is drawn inside the overlay. Window coordinates shift it
          // and stop a corner short of the screen.
          const next = {
            x: pageX - originX,
            y: pageY - originY,
            width: pageWidth,
            height: pageHeight,
          };
          const clipped = clipCaptureFrame(
            next,
            measuredHostHeight,
            reservedRef.current,
          );
          setHostHeight(measuredHostHeight);
          setFrame(next);
          setRect(current =>
            adjusted.current
              ? clampCaptureRect(current, clipped)
              : defaultCaptureRect(clipped),
          );
        };
        const host = hostRef.current;
        if (host && typeof host.measureInWindow === 'function') {
          host.measureInWindow((hostX, hostY, hostWidth, hostHeight) => {
            if (hostWidth <= 0 || hostHeight <= 0) {
              apply(0, 0, pageHeight);
              return;
            }
            apply(hostX, hostY, hostHeight);
          });
          return;
        }
        apply(0, 0, pageHeight);
      });
    };
    const page = feedbackPageRef.current;
    if (!page || typeof page.measureInWindow !== 'function') {
      return;
    }
    if (typeof requestAnimationFrame !== 'function') {
      measure();
      return () => {
        cancelled = true;
      };
    }
    measure();
    const frameId = requestAnimationFrame(measure);
    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
    };
  }, [shot, viewport.width, viewport.height]);

  useEffect(() => {
    if (shot) {
      return;
    }
    setRect(current =>
      adjusted.current
        ? clampCaptureRect(current, bounds)
        : defaultCaptureRect(bounds),
    );
  }, [bounds.x, bounds.y, bounds.width, bounds.height, shot]);

  const markAdjusted = () => {
    adjusted.current = true;
  };
  const movePan = useRectPan(
    moveCaptureRect,
    rectRef,
    boundsRef,
    setRect,
    markAdjusted,
  );
  const nwPan = useRectPan(
    (origin, dx, dy, bounds) => resizeCaptureRect(origin, dx, dy, 'nw', bounds),
    rectRef,
    boundsRef,
    setRect,
    markAdjusted,
  );
  const nePan = useRectPan(
    (origin, dx, dy, bounds) => resizeCaptureRect(origin, dx, dy, 'ne', bounds),
    rectRef,
    boundsRef,
    setRect,
    markAdjusted,
  );
  const swPan = useRectPan(
    (origin, dx, dy, bounds) => resizeCaptureRect(origin, dx, dy, 'sw', bounds),
    rectRef,
    boundsRef,
    setRect,
    markAdjusted,
  );
  const sePan = useRectPan(
    (origin, dx, dy, bounds) => resizeCaptureRect(origin, dx, dy, 'se', bounds),
    rectRef,
    boundsRef,
    setRect,
    markAdjusted,
  );
  const nPan = useRectPan(
    (origin, dx, dy, bounds) => resizeCaptureRect(origin, dx, dy, 'n', bounds),
    rectRef,
    boundsRef,
    setRect,
    markAdjusted,
  );
  const ePan = useRectPan(
    (origin, dx, dy, bounds) => resizeCaptureRect(origin, dx, dy, 'e', bounds),
    rectRef,
    boundsRef,
    setRect,
    markAdjusted,
  );
  const sPan = useRectPan(
    (origin, dx, dy, bounds) => resizeCaptureRect(origin, dx, dy, 's', bounds),
    rectRef,
    boundsRef,
    setRect,
    markAdjusted,
  );
  const wPan = useRectPan(
    (origin, dx, dy, bounds) => resizeCaptureRect(origin, dx, dy, 'w', bounds),
    rectRef,
    boundsRef,
    setRect,
    markAdjusted,
  );

  const dismiss = () => {
    if (settled.current) {
      return;
    }
    settled.current = true;
    busy.current = true;
    onDismiss();
  };

  const fail = () => {
    if (settled.current) {
      return;
    }
    dropQueuedFeedbackNavigation();
    showToast.error(CAPTURE_FAILED);
    setSnapping(false);
    dismiss();
  };

  const finishCrop = async () => {
    // Jest does not attach refs on these views. Production captures the crop view.
    const node =
      feedbackCropRef.current ??
      (process.env.NODE_ENV === 'test' ? feedbackPageRef.current : null);
    if (!node) {
      fail();
      return;
    }
    try {
      const cropped = pngText(
        await captureRef(node, { format: 'png', result: 'base64' }),
      );
      const file = feedbackFilePayload({
        fileName: 'screen.png',
        mimeType: 'image/png',
        fileSizeBytes: decodedBase64Bytes(cropped),
        fileData: cropped,
      });
      if (settled.current) {
        return;
      }
      settled.current = true;
      busy.current = true;
      dropQueuedFeedbackNavigation();
      setSnapping(false);
      onDone(file);
    } catch (error) {
      if (settled.current) {
        return;
      }
      if (error instanceof FeedbackFileError) {
        dropQueuedFeedbackNavigation();
        showToast.error(error.message);
        setSnapping(false);
        dismiss();
        return;
      }
      fail();
    }
  };

  const handleCropLoad = () => {
    if (cropStarted.current) {
      return;
    }
    cropStarted.current = true;
    requestAnimationFrame(() => {
      if (settled.current) {
        return;
      }
      finishCrop().catch(() => undefined);
    });
  };

  finishCropRef.current = finishCrop;
  const dismissRef = useRef(dismiss);
  dismissRef.current = dismiss;
  const failRef = useRef(fail);
  failRef.current = fail;

  useEffect(() => {
    if (!shot || cropStarted.current || process.env.NODE_ENV !== 'test') {
      return;
    }
    cropStarted.current = true;
    finishCropRef.current().catch(() => undefined);
  }, [shot]);

  useEffect(() => {
    if (!shot || process.env.NODE_ENV === 'test') {
      return;
    }
    const timer = setTimeout(() => {
      if (!settled.current) {
        failRef.current();
      }
    }, CAPTURE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [shot]);

  useEffect(() => {
    if (
      Platform.OS !== 'android' ||
      typeof BackHandler?.addEventListener !== 'function'
    ) {
      return;
    }
    const subscription = BackHandler.addEventListener(
      'hardwareBackPress',
      () => {
        dismissRef.current();
        return true;
      },
    );
    return () => subscription.remove();
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
      return;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        dismissRef.current();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleDone = async () => {
    if (busy.current || settled.current) {
      return;
    }
    busy.current = true;
    const page = feedbackPageRef.current;
    if (!page) {
      fail();
      return;
    }
    setSnapping(true);
    try {
      const full = pngText(
        await captureRef(page, { format: 'png', result: 'base64' }),
      );
      if (settled.current) {
        return;
      }
      if (full === '') {
        throw new Error('empty-capture');
      }
      cropStarted.current = false;
      setShot(full);
    } catch {
      fail();
    }
  };

  const localX = rect.x - frame.x;
  const localY = rect.y - frame.y;
  const corner = (x: number, y: number) => ({
    left: x - HANDLE_HIT / 2,
    top: y - HANDLE_HIT / 2,
  });
  const webCursor = (cursor: string) =>
    Platform.OS === 'web' ? { cursor: cursor as 'pointer' } : null;
  const edges: Array<{
    id: CaptureHandle;
    pan: typeof movePan;
    box: { left: number; top: number; width: number; height: number };
  }> = [
    {
      id: 'n',
      pan: nPan,
      box: {
        left: rect.x,
        top: rect.y - EDGE / 2,
        width: rect.width,
        height: EDGE,
      },
    },
    {
      id: 'e',
      pan: ePan,
      box: {
        left: rect.x + rect.width - EDGE / 2,
        top: rect.y,
        width: EDGE,
        height: rect.height,
      },
    },
    {
      id: 's',
      pan: sPan,
      box: {
        left: rect.x,
        top: rect.y + rect.height - EDGE / 2,
        width: rect.width,
        height: EDGE,
      },
    },
    {
      id: 'w',
      pan: wPan,
      box: {
        left: rect.x - EDGE / 2,
        top: rect.y,
        width: EDGE,
        height: rect.height,
      },
    },
  ];
  const handles: Array<{
    id: CaptureHandle;
    pan: typeof movePan;
    left: number;
    top: number;
  }> = [
    { id: 'nw', pan: nwPan, ...corner(rect.x, rect.y) },
    { id: 'ne', pan: nePan, ...corner(rect.x + rect.width, rect.y) },
    { id: 'sw', pan: swPan, ...corner(rect.x, rect.y + rect.height) },
    {
      id: 'se',
      pan: sePan,
      ...corner(rect.x + rect.width, rect.y + rect.height),
    },
  ];

  return (
    <View
      ref={hostRef}
      style={styles.host}
      testID="feedback-capture-overlay"
      accessibilityViewIsModal
    >
      {shot ? (
        <View
          ref={feedbackCropRef}
          collapsable={false}
          style={[
            styles.crop,
            {
              left: rect.x,
              top: rect.y,
              width: rect.width,
              height: rect.height,
            },
          ]}
        >
          <Image
            testID="feedback-capture-crop-source"
            source={{ uri: `data:image/png;base64,${shot}` }}
            resizeMode="stretch"
            fadeDuration={0}
            style={[
              styles.cropImage,
              {
                left: -localX,
                top: -localY,
                width: frame.width,
                height: frame.height,
              },
            ]}
            onLoad={handleCropLoad}
            onError={() => {
              if (cropStarted.current || settled.current) {
                return;
              }
              cropStarted.current = true;
              fail();
            }}
          />
        </View>
      ) : null}
      <View
        pointerEvents="none"
        style={[styles.dim, styles.dimTop, { height: Math.max(rect.y, 0) }]}
      />
      <View
        pointerEvents="none"
        style={[
          styles.dim,
          styles.dimLeft,
          { top: rect.y, width: Math.max(rect.x, 0), height: rect.height },
        ]}
      />
      <View
        pointerEvents="none"
        style={[
          styles.dim,
          styles.dimRight,
          { top: rect.y, left: rect.x + rect.width, height: rect.height },
        ]}
      />
      <View
        pointerEvents="none"
        style={[styles.dim, styles.dimBottom, { top: rect.y + rect.height }]}
      />
      <View
        pointerEvents="none"
        style={[
          styles.rectOutline,
          {
            left: rect.x - 3,
            top: rect.y - 3,
            width: rect.width + 6,
            height: rect.height + 6,
          },
        ]}
      />
      <View
        accessibilityLabel="Capture area"
        testID="feedback-capture-area"
        style={[
          styles.rect,
          {
            left: rect.x,
            top: rect.y,
            width: rect.width,
            height: rect.height,
          },
        ]}
        {...movePan.panHandlers}
      />
      {edges.map(edge => (
        <View
          key={edge.id}
          accessibilityLabel="Resize capture area"
          testID={`feedback-capture-edge-${edge.id}`}
          style={[
            styles.edge,
            edge.box,
            webCursor(
              edge.id === 'n' || edge.id === 's' ? 'ns-resize' : 'ew-resize',
            ),
          ]}
          {...edge.pan.panHandlers}
        />
      ))}
      {handles.map(handle => (
        <View
          key={handle.id}
          accessibilityLabel="Resize capture area"
          testID={`feedback-capture-handle-${handle.id}`}
          style={[
            styles.handleHit,
            { left: handle.left, top: handle.top },
            webCursor(
              handle.id === 'nw' || handle.id === 'se'
                ? 'nwse-resize'
                : 'nesw-resize',
            ),
          ]}
          {...handle.pan.panHandlers}
        >
          <View pointerEvents="none" style={styles.handle} />
        </View>
      ))}
      <View
        pointerEvents="box-none"
        testID="feedback-capture-bar"
        onLayout={event => {
          const next = event.nativeEvent.layout.height;
          if (next > 0) {
            setBarHeight(current => (current === next ? current : next));
          }
        }}
        style={[styles.bar, { bottom: Math.max(insets.bottom, spacing.md) }]}
      >
        <Text pointerEvents="none" style={styles.hint}>
          Drag an edge or corner to resize
        </Text>
        <View style={styles.actions}>
          <TouchableOpacity
            testID="feedback-capture-cancel"
            accessibilityRole="button"
            accessibilityLabel="Cancel"
            onPress={dismiss}
            style={styles.cancel}
          >
            <Text style={styles.cancelText}>Cancel</Text>
          </TouchableOpacity>
          <TouchableOpacity
            testID="feedback-capture-done"
            accessibilityRole="button"
            accessibilityLabel="Done"
            onPress={() => {
              handleDone().catch(() => undefined);
            }}
            disabled={snapping}
            style={styles.done}
          >
            {snapping ? (
              <ActivityIndicator size="small" color={colors.textLight} />
            ) : (
              <Text style={styles.doneText}>Done</Text>
            )}
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

export function FeedbackCaptureHost(): React.ReactElement | null {
  const request = useFeedbackCaptureRequest();
  useEffect(() => {
    if (!request) {
      return;
    }
    beginFeedbackCapture();
    return () => {
      endFeedbackCapture();
    };
  }, [request]);

  if (!request) {
    return null;
  }
  // Web keeps the crop. Mobile photographs the app window. The system
  // screenshot buttons cannot be invoked by an app, and Android's
  // full-display capture can include notifications and other apps.
  if (Platform.OS === 'web') {
    return (
      <ScreenCapture onDone={request.onDone} onDismiss={request.onDismiss} />
    );
  }
  return (
    <DeviceScreenCapture
      onDone={request.onDone}
      onDismiss={request.onDismiss}
    />
  );
}

const styles = StyleSheet.create({
  host: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 1000,
    elevation: 1000,
    userSelect: 'none',
  },
  crop: {
    position: 'absolute',
    overflow: 'hidden',
  },
  cropImage: {
    position: 'absolute',
  },
  dim: {
    position: 'absolute',
    backgroundColor: colors.overlayBackdrop,
  },
  dimTop: {
    top: 0,
    left: 0,
    right: 0,
  },
  dimLeft: {
    left: 0,
  },
  dimRight: {
    right: 0,
  },
  dimBottom: {
    left: 0,
    right: 0,
    bottom: 0,
  },
  rectOutline: {
    position: 'absolute',
    borderWidth: 3,
    borderColor: colors.textLight,
    backgroundColor: 'transparent',
  },
  rect: {
    position: 'absolute',
    zIndex: 1,
    borderWidth: 2,
    borderStyle: 'dotted',
    borderColor: colors.primary,
    backgroundColor: 'transparent',
  },
  edge: {
    position: 'absolute',
    zIndex: 2,
    backgroundColor: 'transparent',
  },
  handleHit: {
    position: 'absolute',
    zIndex: 3,
    width: HANDLE_HIT,
    height: HANDLE_HIT,
    alignItems: 'center',
    justifyContent: 'center',
  },
  handle: {
    width: HANDLE,
    height: HANDLE,
    borderRadius: spacing.xs,
    backgroundColor: colors.primary,
    borderWidth: 2,
    borderColor: colors.textLight,
  },
  bar: {
    position: 'absolute',
    left: spacing.md,
    right: spacing.md,
    zIndex: 4,
    elevation: 4,
    minHeight: spacing.formControlHeight + spacing.md,
    paddingHorizontal: spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: spacing.formControlBorderRadius,
    backgroundColor: colors.backgroundForm,
    gap: spacing.md,
  },
  hint: {
    flex: 1,
    fontSize: fontSize.formLabel,
    lineHeight: lineHeight.formLabel,
    color: colors.textSecondary,
    fontWeight: fontWeight.regular,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  cancel: {
    height: spacing.formControlHeight,
    paddingHorizontal: spacing.formCompactActionPaddingHorizontal,
    borderRadius: spacing.formControlBorderRadius,
    borderWidth: 1,
    borderColor: colors.inputBorderNeutral,
    backgroundColor: colors.backgroundSecondary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelText: {
    fontSize: fontSize.formLabel,
    lineHeight: lineHeight.formLabel,
    color: colors.textSecondary,
    fontWeight: fontWeight.medium,
    includeFontPadding: false,
  },
  done: {
    height: spacing.formControlHeight,
    minWidth: spacing.formControlHeight * 2,
    paddingHorizontal: spacing.formCompactActionPaddingHorizontal,
    borderRadius: spacing.formControlBorderRadius,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  doneText: {
    fontSize: fontSize.formLabel,
    lineHeight: lineHeight.formLabel,
    color: colors.textLight,
    fontWeight: fontWeight.semibold,
    includeFontPadding: false,
  },
});
